import { useState } from "react";
import type { ProductGroup } from "../lib/types";
import { useCart } from "../lib/cartStore";
import { useSubscriptions } from "../lib/subscriptionContext";
import {
  displayProductName,
  formatWholeDollars,
  getProductTiers,
  isStudentProduct,
  isNswStageProduct,
  isVceSchoolLicence,
  VCE_SCHOOL_LICENCE_QUANTITY,
} from "../lib/products";

interface ProductCardProps {
  group: ProductGroup;
}

/**
 * Format a date string (YYYY-MM-DD) for display
 */
function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
}

// Get default quantity for a product based on type
function getDefaultQuantity(group: ProductGroup): number {
  // A VCE school licence is always its 99 included teacher seats — never the fallback 2.
  if (isVceSchoolLicence(group)) return VCE_SCHOOL_LICENCE_QUANTITY;
  if (isStudentProduct(group)) return 1;
  if (isNswStageProduct(group)) return 5;
  if (group.id === "hsc_std2") return 4;
  if (group.id === "hsc_ext2_slv") return 1;
  return 2;
}

/**
 * One product as a compact row: name, variant pills, pricing text, stepper, action.
 * Keeps the `.product-card` / `button.btn-full` / stepper aria-label hooks the
 * deployed E2E harness relies on.
 *
 * VCE school licences are the exception: one fixed price, Add/Remove only — no
 * stepper, no variant pills, and never a teacher count on screen.
 */
export function ProductCard({ group }: ProductCardProps) {
  const { entries, addToCart, updateQuantity, updateVariant, removeFromCart } = useCart();
  const { getSubscriptionForProduct, getVariantFromSubscription } = useSubscriptions();

  // Check if school has existing subscription for this product
  const existingSubscription = getSubscriptionForProduct(group.id);
  const isOwned = !!existingSubscription;

  // For owned products, lock to the subscription's variant
  const ownedVariant = existingSubscription
    ? getVariantFromSubscription(existingSubscription)
    : undefined;

  // Local state for quantity and variant (before adding to cart)
  // For owned products, start at 0 (no additional teachers selected yet)
  const defaultQty = getDefaultQuantity(group);
  const [localQuantity, setLocalQuantity] = useState(isOwned ? 0 : defaultQty);
  const [localVariant, setLocalVariant] = useState(ownedVariant || group.defaultVariant || "");

  // Check if this product is already in cart
  const cartEntry = entries.find((e) => e.groupId === group.id);
  const isInCart = !!cartEntry;

  // Determine which variant is active
  // For owned products, always use the subscription's variant
  const activeVariant = isOwned
    ? ownedVariant || group.defaultVariant
    : isInCart
      ? cartEntry?.variantId || group.defaultVariant
      : localVariant || group.defaultVariant;

  // Get pricing info for display based on active variant
  const tiers = getProductTiers(group, activeVariant);
  const initialTier = tiers.find((t) => t.tierType === "initial");
  const additionalTier = tiers.find((t) => t.tierType === "additional");

  // Check if product has multiple variants (Bronze/Silver)
  const hasVariants = group.variants && group.variants.length > 1;

  // Check if this is a student product (different display and pricing labels)
  const isStudent = isStudentProduct(group);

  // VCE school licence: fixed price per course, quantity pinned at 99 by the cart store
  const isLicence = isVceSchoolLicence(group);

  const displayQuantity = isInCart ? cartEntry!.quantity : localQuantity;
  const firstPrice = initialTier?.unitPriceExGstCents || 0;
  const additionalPrice = additionalTier?.unitPriceExGstCents || 0;

  // Get students per block for display (for student products)
  const initialStudentsPerBlock = initialTier?.studentsPerBlock || 0;
  const additionalStudentsPerBlock = additionalTier?.studentsPerBlock || 0;

  // D1: static pricing text following the active variant; totals live in the cart.
  // Owned products only buy additional seats, so there is no first-teacher price.
  let pricingText: string;
  if (isLicence) {
    pricingText = `${formatWholeDollars(firstPrice)}/yr — includes all teachers at your school`;
  } else if (isStudent) {
    const additional = `${formatWholeDollars(additionalPrice)} each ${additionalStudentsPerBlock} additional students`;
    pricingText = isOwned
      ? additional
      : `${formatWholeDollars(firstPrice)} includes ${initialStudentsPerBlock} students + ${additional}`;
  } else {
    pricingText = isOwned
      ? `${formatWholeDollars(additionalPrice)} each additional teacher`
      : `${formatWholeDollars(firstPrice)} first teacher + ${formatWholeDollars(additionalPrice)} each additional`;
  }

  const displayName = displayProductName(group.name);

  const handleAddToCart = () => {
    if (isLicence) {
      // One licence per course. An active licence is never topped up from here.
      if (!isOwned) addToCart(group, VCE_SCHOOL_LICENCE_QUANTITY, undefined, false);
    } else if (isOwned) {
      // For owned products, pass isAddingToExisting flag and use locked variant
      if (localQuantity > 0) {
        addToCart(group, localQuantity, ownedVariant || group.defaultVariant, true);
        setLocalQuantity(0);
      }
    } else {
      addToCart(group, localQuantity, localVariant || group.defaultVariant, false);
      setLocalQuantity(defaultQty);
    }
  };

  const handleUpdateQuantity = (delta: number) => {
    if (isInCart) {
      const newQty = cartEntry!.quantity + delta;
      const minQty = isOwned ? 0 : 1;
      if (newQty < minQty) {
        // Remove from cart and reset local state
        removeFromCart(group.id);
        setLocalQuantity(isOwned ? 0 : defaultQty);
      } else {
        updateQuantity(group.id, newQty);
      }
    } else {
      const minQty = isOwned ? 0 : 1;
      setLocalQuantity(Math.max(minQty, localQuantity + delta));
    }
  };

  const handleVariantChange = (variantId: string) => {
    // Don't allow variant change for owned products
    if (isOwned) return;

    if (isInCart) {
      updateVariant(group.id, variantId);
    } else {
      setLocalVariant(variantId);
    }
  };

  // Determine if Add to cart button should be disabled.
  // An owned school licence already covers every teacher, so there is nothing to add.
  const addButtonDisabled = isLicence ? isOwned : isOwned && displayQuantity === 0;

  return (
    <div
      className={`product-card ${isInCart ? "product-card--in-cart" : ""} ${isOwned ? "product-card--owned" : ""} ${isLicence ? "product-card--licence" : ""}`}
    >
      <div className="product-row-name">
        <h3 className="product-card-name" title={group.name}>{displayName}</h3>

        {/* Owned marker + subscription info - teacher products only (student products don't have seat tracking) */}
        {isOwned && existingSubscription && !isStudent && (
          <div className="product-card-subscription-info">
            <span className="product-card-badge">✓ ACTIVE</span>
            <span className="subscription-seats">
              {isLicence ? "School licence" : `${existingSubscription.seats} seats`}
            </span>
            <span className="subscription-separator">·</span>
            <span className="subscription-expiry">
              Expires {formatDate(existingSubscription.subscription_ends)}
            </span>
          </div>
        )}
      </div>

      {/* Variant pills - owned products stay locked to their subscription's variant */}
      <div className="product-row-variants">
        {!isOwned && hasVariants && !isLicence && (
          <div className="product-card-tiers">
            {group.variants!.map((variant) => (
              <button
                key={variant.id}
                type="button"
                className={`tier-pill ${activeVariant === variant.id ? "active" : ""}`}
                onClick={() => handleVariantChange(variant.id)}
                aria-pressed={activeVariant === variant.id}
              >
                {variant.name}
                {variant.id === group.defaultVariant && (
                  <span className="star" title="Recommended">★</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="product-row-pricing">{pricingText}</div>

      {/* Quantity stepper - teachers, or students for student products; never for a school licence */}
      {!isLicence && (
        <div className="quantity-stepper">
          <button
            type="button"
            className="stepper-btn"
            onClick={() => handleUpdateQuantity(-1)}
            disabled={!isInCart && displayQuantity <= (isOwned ? 0 : 1)}
            aria-label="Decrease quantity"
          >
            −
          </button>
          <span className="stepper-value">
            {isStudent
              ? isOwned
                ? displayQuantity * additionalStudentsPerBlock
                : initialStudentsPerBlock + Math.max(0, displayQuantity - 1) * additionalStudentsPerBlock
              : displayQuantity}
          </span>
          <button
            type="button"
            className="stepper-btn"
            onClick={() => handleUpdateQuantity(1)}
            aria-label="Increase quantity"
          >
            +
          </button>
        </div>
      )}

      {/* Action Button */}
      {isInCart ? (
        <button
          type="button"
          className="btn btn-added btn-full"
          title="Click to remove from cart"
          aria-label={`Added – remove ${displayName} from cart`}
          onClick={() => {
            removeFromCart(group.id);
            setLocalQuantity(isOwned ? 0 : defaultQty);
          }}
        >
          ✓ Added
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-add btn-full"
          aria-label={`Add ${displayName} to cart`}
          onClick={handleAddToCart}
          disabled={addButtonDisabled}
        >
          + Add
        </button>
      )}
    </div>
  );
}
